import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { Stage, Layer, Transformer, Rect, Group } from 'react-konva';
import Konva from 'konva';
import {
  useTemplateStore,
  useVisibleSortedElements
} from '../hooks/useTemplateStore';
import { useTransformer } from '../hooks/useTransformer';
import { useElementRenderer } from '../hooks/useElementRenderer';
import { useElementTransform } from '../hooks/useElementTransform';
import { useSnapping } from '../hooks/useSnapping';
import { SnapLines } from './snap-lines';
import { Camera } from '../utils/cameraUtils';
import { FileDropZone } from '../../components/FileDropZone';
import { toast } from "@/lib/toast";
import { useMediaLibraryUpload } from '@/lib/api/requests/media-library.requests';
import { useTemplatePermissions } from '../hooks/useTemplatePermissions';
import { ElementContextMenu } from './element-context-menu';

/**
 * Canvas component props
 */
interface CanvasProps {
  templateWidth: number;   // Display width
  templateHeight: number;  // Display height
  camera: Camera;
  stageRef: React.RefObject<Konva.Stage>;
  /** Wheel event handler */
  onWheel: (e: Konva.KonvaEventObject<WheelEvent>) => void;
  /** Mouse down event handler */
  onMouseDown: (e: Konva.KonvaEventObject<MouseEvent>) => void;
}

export const Canvas: React.FC<CanvasProps> = ({ 
  templateWidth, 
  templateHeight,
  camera, 
  stageRef, 
  onWheel, 
  onMouseDown 
}) => {
  // Get elements and store actions
  const elements = useVisibleSortedElements();
  const selectElement = useTemplateStore(state => state.selectElement);
  const addElement = useTemplateStore(s => s.addElement);

  const { canEditTemplate: canAddElements } = useTemplatePermissions();
  
  const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/heic', 'image/heif', 'features.svg_support']

  // Upload mutation for media library
  const uploadMutation = useMediaLibraryUpload({
    onSuccess: (data) => {
      if (!canAddElements) {
        toast.error('Nur berechtigte Nutzer können Elemente hinzufügen');
        return;
      }
      if (data && data.displayName && data.mediaId) {
        // Create a temporary image to get dimensions
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const MAX_DIMENSION = 500;
          let newWidth = img.width;
          let newHeight = img.height;

          if (img.width > MAX_DIMENSION || img.height > MAX_DIMENSION) {
            if (img.width > img.height) {
              newWidth = MAX_DIMENSION;
              newHeight = img.height * (MAX_DIMENSION / img.width);
            } else {
              newHeight = MAX_DIMENSION;
              newWidth = img.width * (MAX_DIMENSION / img.height);
            }
          }

          // Add the element to the canvas using the uploaded media data
          if (data.mimeType === 'image/svg+xml') {
            addElement('svg', {
              name: data.displayName,
              source: {
                mode: "static",
                mediaType: "image",
                staticValue: {
                  mediaId: data.mediaId,
                  path: data.url,
                }
              },
              width: newWidth,
              height: newHeight,
              fillMode: 'original',
              colorMapping: {}
            });
          } else {
            addElement('image', {
              name: data.displayName,
              source: {
                mode: "static",
                mediaType: "image",
                staticValue: {
                  mediaId: data.mediaId,
                  path: data.url,
                }
              },
              width: newWidth,
              height: newHeight
            });
          }

          toast.success(`${data.displayName} wurde hinzugefügt`);
        };
        
        img.onerror = () => {
          toast.error(`Fehler beim Laden von ${data.displayName}`);
        };
        
        img.src = data.url;
      } else {
        console.error('Upload succeeded but returned invalid data:', data);
        toast.error("Upload fehlgeschlagen", {
          description: "Ungültige Antwort vom Server - Datei möglicherweise nicht gespeichert",
        });
      }
    },
    onError: (error) => {
      toast.error("Upload fehlgeschlagen", {
        description: error.message || "Ein unbekannter Fehler ist aufgetreten",
        error,
      });
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Custom hooks
  const { snapLines, updateStage, handleDragSnapping, handleResizeSnapping, clearSnapLines } = useSnapping(camera, templateWidth, templateHeight);
  const {
    handleElementDragMove,
    handleElementDragEnd,
    handleElementTransform,
    handleElementTransformEnd
  } = useElementTransform(stageRef.current, {
    handleDragSnapping,
    handleResizeSnapping,
    clearSnapLines
  });
  const { transformerRef } = useTransformer(stageRef.current);
  const { renderElement } = useElementRenderer();

  // Update stage reference when it becomes available
  useEffect(() => {
    if (stageRef.current) {
      updateStage(stageRef.current);
    }
  }, [updateStage, stageRef]);

  // Event handlers
  const eventHandlers = {
    onDragMove: handleElementDragMove,
    onDragEnd: handleElementDragEnd,
    onTransform: handleElementTransform,
    onTransformEnd: handleElementTransformEnd
  };

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    // Deselect elements when clicking on the stage
    if (e.target === e.target.getStage()) {
      selectElement(null);
    }
  };

  const handleStageContextMenu = (e: Konva.KonvaEventObject<PointerEvent>) => {
    // Prevent default browser menu handled by Konva, 
    // but we want the event to bubble to the container for Shadcn context menu.
    // However, if we click background, we want to deselect.
    if (e.target === e.target.getStage()) {
      selectElement(null);
    }
    // We do NOT call e.evt.preventDefault() because we want the wrapper to catch it.
  };

  /**
   * Handle dropped image files and add them as image elements to the canvas
   */
  const handleImageDrop = async (files: File[]) => {
      if (!canAddElements) {
        toast.error('Nur berechtigte Nutzer können Elemente hinzufügen');
        return;
      }
    // Filter for image files only
    const imageFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) return false;
      return true;
    });
    
    if (imageFiles.length === 0) {
      toast.error("Keine gültigen Bilddateien gefunden", {
        description: "Nur JPG, PNG, WEBP, SVG, HEIC und HEIF Dateien sind erlaubt",
      });
      return;
    }

    if (imageFiles.length !== files.length) {
      toast.warning("Einige Dateien wurden übersprungen", {
        description: "Nur Bilddateien sind erlaubt",
      });
    }

    // Check file sizes (5MB limit)
    const oversizedFiles = imageFiles.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error("Dateien zu groß", {
        description: `${oversizedFiles.length} Datei(en) überschreiten das 5MB Limit`,
      });
      return;
    }

    try {
      // Upload files sequentially
      for (const file of imageFiles) {
        await uploadMutation.mutateAsync({ file });
      }
      
      toast.success("Alle Dateien hochgeladen", {
        description: `${imageFiles.length} Bilddatei(en) erfolgreich hinzugefügt`,
      });
    } catch (error) {
      console.error("Upload failed:", error);
      // Error handling is done in the mutation's onError callback
      // This is redundant as onError in mutation handles it, but kept for clarity
    }
  };

  return (
    <ElementContextMenu className="w-full h-full block">
      <FileDropZone
        acceptedFileTypes={acceptedFileTypes}
        onFileDrop={handleImageDrop}
        overlayText="Bilder hier ablegen zum Hinzufügen"
        className="w-full h-full"
        disableClick={true}
        disabled={!canAddElements}
      >
        <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
          <Stage
            ref={stageRef}
            width={containerDimensions.width}
            height={containerDimensions.height}
            scaleX={camera.scale}
            scaleY={camera.scale}
            x={camera.position.x}
            y={camera.position.y}
            onMouseDown={(e) => {
              handleStageClick(e);
              onMouseDown(e);
            }}
            onTouchStart={handleStageClick}
            onContextMenu={handleStageContextMenu}
            onWheel={onWheel}
          >
            <Layer>
              {/* White background for the template */}
              <Rect
                id='template-background' /* Do not change id! */
                x={0}
                y={0}
                width={templateWidth}
                height={templateHeight}
                fill="white"
                shadowBlur={5}
                cornerRadius={16}
                shadowColor="rgba(0,0,0,0.2)"
                listening={false}
              />
              <Group
                clipFunc={(ctx: any) => {
                  const r = 16;
                  ctx.beginPath();
                  ctx.moveTo(r, 0);
                  ctx.lineTo(templateWidth - r, 0);
                  ctx.quadraticCurveTo(templateWidth, 0, templateWidth, r);
                  ctx.lineTo(templateWidth, templateHeight - r);
                  ctx.quadraticCurveTo(templateWidth, templateHeight, templateWidth - r, templateHeight);
                  ctx.lineTo(r, templateHeight);
                  ctx.quadraticCurveTo(0, templateHeight, 0, templateHeight - r);
                  ctx.lineTo(0, r);
                  ctx.quadraticCurveTo(0, 0, r, 0);
                  ctx.closePath();
                }}
              >
                {elements.map((element) => (
                  <React.Fragment key={element.id}>
                    {renderElement(element, eventHandlers)}
                  </React.Fragment>
                ))}
              </Group>

              <SnapLines snapLines={snapLines} />

              <Transformer
                ref={transformerRef}
                keepRatio={false} // Allow free transform for all elements
                boundBoxFunc={(oldBox, newBox) => {
                  // Limit resize
                  if (newBox.width < 5 || newBox.height < 5) {
                    return oldBox;
                  }
                  return newBox;
                }}
              />
            </Layer>
          </Stage>
        </div>
      </FileDropZone>
    </ElementContextMenu>
  );
};
